import type { CvData } from '@/types/cv';

const AVATAR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="256" height="256">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#a78bfa"/>
      <stop offset="100%" stop-color="#ec4899"/>
    </linearGradient>
    <linearGradient id="skin" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#fde7d2"/>
      <stop offset="100%" stop-color="#f4caa6"/>
    </linearGradient>
    <linearGradient id="hair" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#4b3b2a"/>
      <stop offset="100%" stop-color="#2e2418"/>
    </linearGradient>
    <linearGradient id="shirt" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#1f2937"/>
      <stop offset="100%" stop-color="#111827"/>
    </linearGradient>
  </defs>
  <rect width="256" height="256" fill="url(#bg)"/>
  <path d="M0 256 C 40 200, 100 175, 128 175 C 156 175, 216 200, 256 256 Z" fill="url(#shirt)"/>
  <path d="M128 188 L100 220 L128 235 L156 220 Z" fill="url(#skin)"/>
  <path d="M76 124 C 76 165, 100 198, 128 198 C 156 198, 180 165, 180 124 C 180 95, 156 78, 128 78 C 100 78, 76 95, 76 124 Z" fill="url(#skin)"/>
  <path d="M80 118 C 78 80, 102 60, 128 60 C 158 60, 184 82, 182 124 C 180 110, 172 100, 160 96 C 148 110, 122 116, 96 110 C 88 112, 82 116, 80 118 Z" fill="url(#hair)"/>
  <path d="M76 118 C 70 130, 70 150, 78 162 L 86 138 Z" fill="url(#hair)"/>
  <ellipse cx="108" cy="138" rx="3.5" ry="4.5" fill="#3b2a1a"/>
  <ellipse cx="148" cy="138" rx="3.5" ry="4.5" fill="#3b2a1a"/>
  <path d="M112 162 Q 128 174 144 162" stroke="#c97b6b" stroke-width="3" fill="none" stroke-linecap="round"/>
  <ellipse cx="98" cy="150" rx="6" ry="3" fill="#f0a8a0" opacity="0.5"/>
  <ellipse cx="158" cy="150" rx="6" ry="3" fill="#f0a8a0" opacity="0.5"/>
</svg>`;

const SAMPLE_PHOTO_URL = `data:image/svg+xml;utf8,${encodeURIComponent(AVATAR_SVG)}`;

export const samplePhotoUrl = SAMPLE_PHOTO_URL;

export function buildSampleCv(templateId: string, color: string): CvData {
  return {
    id: `sample-${templateId}`,
    metadata: {
      createdAt: '2025-01-01T00:00:00.000Z',
      updatedAt: '2025-01-01T00:00:00.000Z',
      schemaVersion: 1,
      name: 'Sample',
    },
    personalInfo: {
      firstName: 'Anna',
      lastName: 'Kowalska',
      title: 'Senior Product Designer',
      email: 'anna.kowalska@example.com',
      phone: '+48 600 123 456',
      address: { city: 'Warszawa', country: 'Polska' },
      summary:
        'Projektantka produktów cyfrowych z ponad 8-letnim doświadczeniem w tworzeniu skalowalnych systemów designu oraz prowadzeniu zespołów kreatywnych. Specjalizuję się w produktach B2B SaaS i fintech.',
      websites: [
        { id: 'w1', label: 'Portfolio', url: 'https://annakowalska.design' },
        { id: 'w2', label: 'LinkedIn', url: 'https://linkedin.com/in/annakowalska' },
      ],
    },
    experience: [
      {
        id: 'e1',
        company: 'Stripe',
        position: 'Senior Product Designer',
        startDate: '2022-03',
        endDate: '',
        location: 'Remote',
        description:
          'Prowadzenie projektów wpływających na doświadczenie ponad miliona użytkowników biznesowych w produktach płatniczych.',
        achievements: [
          'Zwiększenie konwersji checkout o 18% przez redesign flow płatności',
          'Wprowadzenie design systemu obniżającego czas dostarczania feature o 35%',
        ],
      },
      {
        id: 'e2',
        company: 'Allegro',
        position: 'Product Designer',
        startDate: '2019-06',
        endDate: '2022-02',
        location: 'Poznań',
        description:
          'Projektowanie sekcji sprzedażowych dla największej platformy e-commerce w regionie.',
        achievements: [
          'Lead designerka projektu Allegro Smart — wzrost retencji o 24%',
          'Wdrożenie A/B testów jako standardu w procesie projektowym',
        ],
      },
      {
        id: 'e3',
        company: 'Netguru',
        position: 'UX Designer',
        startDate: '2017-01',
        endDate: '2019-05',
        location: 'Poznań',
        description: 'Praca z klientami z USA i UK w obszarze fintech, healthcare i mobility.',
      },
    ],
    education: [
      {
        id: 'ed1',
        institution: 'School of Form',
        degree: 'Magister',
        field: 'Communication Design',
        startDate: '2015-09',
        endDate: '2017-06',
      },
      {
        id: 'ed2',
        institution: 'Uniwersytet Warszawski',
        degree: 'Licencjat',
        field: 'Psychologia',
        startDate: '2011-09',
        endDate: '2014-06',
      },
    ],
    skills: [
      { id: 's1', name: 'Figma', level: 'expert', category: 'Tools' },
      { id: 's2', name: 'Design Systems', level: 'expert', category: 'Tools' },
      { id: 's3', name: 'Prototyping', level: 'advanced', category: 'Tools' },
      { id: 's4', name: 'User Research', level: 'advanced', category: 'Research' },
      { id: 's5', name: 'A/B Testing', level: 'intermediate', category: 'Research' },
      { id: 's6', name: 'HTML & CSS', level: 'intermediate', category: 'Frontend' },
    ],
    languages: [
      { id: 'l1', name: 'Polski', level: 'native' },
      { id: 'l2', name: 'Angielski', level: 'C1' },
      { id: 'l3', name: 'Niemiecki', level: 'B1' },
    ],
    projects: [
      {
        id: 'p1',
        name: 'Open Design System',
        description: 'Open-source biblioteka komponentów oparta o tokens.',
        technologies: ['Figma', 'React', 'Storybook'],
        url: 'github.com/anna/ods',
      },
    ],
    certifications: [
      {
        id: 'c1',
        name: 'NN/g UX Master Certification',
        issuer: 'Nielsen Norman Group',
        date: '2023',
      },
    ],
    customSections: [],
    settings: {
      selectedTemplate: templateId,
      color,
      language: 'pl',
      sectionOrder: [
        { key: 'experience', visible: true, order: 0 },
        { key: 'education', visible: true, order: 1 },
        { key: 'skills', visible: true, order: 2 },
        { key: 'languages', visible: true, order: 3 },
        { key: 'projects', visible: true, order: 4 },
        { key: 'certifications', visible: true, order: 5 },
      ],
      modernSidebarStyle: 'dots',
    },
  };
}
